import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, User as UserIcon, PencilSimple, Trash, MagnifyingGlass, Buildings,
  EnvelopeSimple, Phone, Link as LinkIcon, UploadSimple, CheckCircle, CurrencyDollar,
} from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CustomerRevenue from "@/components/CustomerRevenue";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

const emptyForm = {
  name: "", company: "", email: "", phone: "",
  billing_email: "", notes: "", source: "",
  acquisition_campaign_id: "", partner_id: "", referred_by_customer_id: "",
};

export default function Customers() {
  const { currentOrg } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [custDialogTab, setCustDialogTab] = useState("basics");
  const [confirmDelete, setConfirmDelete] = useState(null);

  // CSV import state
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // {total, imported, skipped, skipped_summary, skipped_details}
  const csvInputRef = useRef(null);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast.error("Please pick a .csv file");
      e.target.value = ""; return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("CSV exceeds 5MB limit");
      e.target.value = ""; return;
    }
    setImporting(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/customers/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      await load();
      if (data.skipped === 0) {
        toast.success(`Imported ${data.imported} of ${data.total} customer(s)`);
      } else {
        toast.success(`Imported ${data.imported} of ${data.total} — ${data.skipped} skipped`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, camps, ptns] = await Promise.all([
        api.get("/customers"),
        api.get("/campaigns").catch(() => ({ data: [] })),
        api.get("/partners").catch(() => ({ data: [] })),
      ]);
      setCustomers(c.data);
      setCampaigns(camps.data || []);
      setPartners((ptns.data || []).filter((p) => p.is_active));
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Customers isn't enabled for this workspace");
      } else {
        toast.error("Could not load customers");
      }
      setCustomers([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const filtered = useMemo(() => {
    if (!query.trim()) return customers;
    const q = query.toLowerCase();
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.company || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q)
    );
  }, [customers, query]);

  const openCreate = () => {
    setEditing(null); setForm(emptyForm); setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c.customer_id);
    setForm({
      name: c.name || "", company: c.company || "", email: c.email || "",
      phone: c.phone || "", billing_email: c.billing_email || "",
      notes: c.notes || "", source: c.source || "",
      acquisition_campaign_id: c.acquisition_campaign_id || "",
      partner_id: c.partner_id || "",
      referred_by_customer_id: c.referred_by_customer_id || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      // Explicitly send optional ids ("" clears on edit; null omits on create)
      const payload = {
        ...form,
        acquisition_campaign_id: form.acquisition_campaign_id || (editing ? "" : null),
        partner_id: form.partner_id || (editing ? "" : null),
        referred_by_customer_id: form.referred_by_customer_id || (editing ? "" : null),
      };
      if (editing) {
        const { data } = await api.patch(`/customers/${editing}`, payload);
        setCustomers((all) => all.map((x) => x.customer_id === editing ? data : x));
        toast.success("Customer updated");
      } else {
        // On create, only send optional ids if user picked one
        const createPayload = { ...form };
        if (!form.acquisition_campaign_id) delete createPayload.acquisition_campaign_id;
        if (!form.partner_id) delete createPayload.partner_id;
        if (!form.referred_by_customer_id) delete createPayload.referred_by_customer_id;
        const { data } = await api.post("/customers", createPayload);
        setCustomers((all) => [data, ...all]);
        toast.success("Customer added");
      }
      setDialogOpen(false); setForm(emptyForm); setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/customers/${confirmDelete.customer_id}`);
      setCustomers((all) => all.filter((x) => x.customer_id !== confirmDelete.customer_id));
      toast.success(`Removed ${confirmDelete.name}`);
    } catch { toast.error("Could not remove"); }
    finally { setConfirmDelete(null); }
  };

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="customers-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">Workspace</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Customers</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-lg">
            The people and companies you're doing business with. Won leads convert here — nothing gets deleted from Leads when they do.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleImport}
            data-testid="customers-import-input"
          />
          <button
            data-testid="customers-import"
            onClick={() => csvInputRef.current?.click()}
            disabled={importing}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-graphite-800 px-4 py-2.5 text-sm text-ink-primary hover:bg-white/5 disabled:opacity-60"
            title="Import a CSV with columns: name, company, email, phone, billing_email, notes, source"
          >
            <UploadSimple weight="bold" className="h-4 w-4" />
            {importing ? "Importing…" : "Import CSV"}
          </button>
          <button
            data-testid="customers-add"
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-5 py-2.5 text-sm font-medium hover:bg-neutral-200 transition-colors"
          >
            <Plus weight="bold" className="h-4 w-4" />
            Add customer
          </button>
        </div>
      </div>

      <div className="mt-6 relative max-w-md">
        <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-secondary" />
        <input
          data-testid="customers-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, company, or email"
          className="w-full bg-graphite-800 border border-white/10 rounded-md pl-10 pr-3 py-2 text-sm text-ink-primary placeholder:text-ink-secondary/60 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30"
        />
      </div>

      <div className="mt-6 rounded-md border border-white/10 bg-graphite-900 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-ink-secondary">Loading customers…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center" data-testid="customers-empty">
            <UserIcon weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/50" />
            <div className="mt-4 font-display text-xl tracking-tight">
              {customers.length === 0 ? "No customers yet" : "No matches"}
            </div>
            <p className="mt-2 text-sm text-ink-secondary max-w-sm mx-auto">
              {customers.length === 0
                ? "Add your first customer, or convert a lead from the Leads page."
                : "Try a different search term."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Company</th>
                  <th className="px-5 py-3 font-medium">Contact</th>
                  <th className="px-5 py-3 font-medium">Source</th>
                  <th className="px-5 py-3 font-medium">Campaign</th>
                  <th className="px-5 py-3 font-medium">Partner</th>
                  <th className="px-5 py-3 font-medium">Added</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((c) => (
                  <tr key={c.customer_id} data-testid={`customer-row-${c.customer_id}`} className="hover:bg-white/[0.02]">
                    <td className="px-5 py-3">
                      <div className="text-ink-primary" data-testid={`customer-name-${c.customer_id}`}>{c.name}</div>
                      {c.converted_from_lead_id && (
                        <div
                          data-testid={`customer-from-lead-${c.customer_id}`}
                          className="mt-0.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-300/80"
                        >
                          <LinkIcon weight="duotone" className="h-3 w-3" />
                          From lead
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-ink-secondary">
                      {c.company ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Buildings weight="duotone" className="h-3.5 w-3.5" />
                          {c.company}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-3 text-ink-secondary">
                      <div className="flex flex-col gap-0.5">
                        {c.email && (
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <EnvelopeSimple weight="duotone" className="h-3 w-3" />
                            {c.email}
                          </span>
                        )}
                        {c.phone && (
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <Phone weight="duotone" className="h-3 w-3" />
                            {c.phone}
                          </span>
                        )}
                        {!c.email && !c.phone && "—"}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-ink-secondary">{c.source || "—"}</td>
                    <td className="px-5 py-3 text-ink-secondary" data-testid={`customer-campaign-${c.customer_id}`}>
                      {c.acquisition_campaign_name ? (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border border-emerald-400/20 bg-emerald-400/5 text-emerald-200">
                          {c.acquisition_campaign_name}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-3 text-ink-secondary" data-testid={`customer-partner-${c.customer_id}`}>
                      {c.partner_name ? (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border border-blue-400/25 bg-blue-400/5 text-blue-200">
                          {c.partner_name}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-3 text-ink-secondary">{formatDate(c.created_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`customer-edit-${c.customer_id}`}
                          onClick={() => openEdit(c)}
                          className="p-2 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5"
                          title="Edit"
                        >
                          <PencilSimple weight="bold" className="h-3.5 w-3.5" />
                        </button>
                        <button
                          data-testid={`customer-delete-${c.customer_id}`}
                          onClick={() => setConfirmDelete(c)}
                          className="p-2 rounded-md text-ink-secondary hover:text-accent-danger hover:bg-white/5"
                          title="Remove"
                        >
                          <Trash weight="bold" className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!saving) { setDialogOpen(v); if (!v) { setForm(emptyForm); setEditing(null); setCustDialogTab("basics"); } } }}>
        <DialogContent data-testid="customer-dialog" className="max-w-2xl bg-graphite-900 border-white/10 text-ink-primary max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">
              {editing ? "Edit customer" : "Add customer"}
            </DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Contact + billing details for a business relationship in this workspace.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={custDialogTab} onValueChange={setCustDialogTab} className="w-full">
            <TabsList className={`grid ${editing ? "grid-cols-2" : "grid-cols-1"} bg-graphite-950 border border-white/10`}>
              <TabsTrigger value="basics" data-testid="customer-tab-basics">Details</TabsTrigger>
              {editing && (
                <TabsTrigger value="revenue" data-testid="customer-tab-revenue">
                  <CurrencyDollar weight="duotone" className="h-3.5 w-3.5 mr-1.5" /> Revenue
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="basics" className="mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Name</label>
              <Input data-testid="customer-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={200} className="bg-graphite-950 border-white/10 text-ink-primary" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Company</label>
              <Input data-testid="customer-form-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-graphite-950 border-white/10 text-ink-primary" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Source</label>
              <Input data-testid="customer-form-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Referral, Google, LinkedIn…" className="bg-graphite-950 border-white/10 text-ink-primary" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Acquisition campaign <span className="opacity-60">(optional)</span></label>
              <select data-testid="customer-form-campaign" value={form.acquisition_campaign_id}
                onChange={(e) => setForm({ ...form, acquisition_campaign_id: e.target.value })}
                className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                <option value="">— None —</option>
                {campaigns.map((c) => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
              </select>
              <p className="text-[11px] text-ink-secondary/60">Which campaign brought this customer in. Auto-set when converting a lead that has a campaign linked.</p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Referring partner <span className="opacity-60">(optional)</span></label>
              <select data-testid="customer-form-partner" value={form.partner_id}
                onChange={(e) => setForm({ ...form, partner_id: e.target.value })}
                className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                <option value="">— None —</option>
                {partners.map((p) => <option key={p.partner_id} value={p.partner_id}>{p.name}</option>)}
              </select>
              <p className="text-[11px] text-ink-secondary/60">Referral source or channel partner. Auto-set when converting a lead with a partner linked.</p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Referred by customer <span className="opacity-60">(optional)</span></label>
              <select data-testid="customer-form-referrer" value={form.referred_by_customer_id}
                onChange={(e) => setForm({ ...form, referred_by_customer_id: e.target.value })}
                className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                <option value="">— None —</option>
                {customers
                  .filter((x) => !editing || x.customer_id !== editing)
                  .map((x) => <option key={x.customer_id} value={x.customer_id}>{x.name}{x.company ? ` · ${x.company}` : ""}</option>)}
              </select>
              <p className="text-[11px] text-ink-secondary/60">Which existing customer referred them. Auto-set when converting a referred lead.</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Email</label>
              <Input data-testid="customer-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-graphite-950 border-white/10 text-ink-primary" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Phone</label>
              <Input data-testid="customer-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-graphite-950 border-white/10 text-ink-primary" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Billing email <span className="opacity-60">(optional, if different from contact email)</span></label>
              <Input data-testid="customer-form-billing-email" type="email" value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} className="bg-graphite-950 border-white/10 text-ink-primary" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Notes</label>
              <Textarea data-testid="customer-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} className="bg-graphite-950 border-white/10 text-ink-primary" />
            </div>
          </div>
            </TabsContent>

            {editing && (
              <TabsContent value="revenue" className="mt-4">
                <CustomerRevenue customerId={editing} />
              </TabsContent>
            )}
          </Tabs>

          <DialogFooter className="gap-2">
            <button
              data-testid="customer-form-cancel"
              onClick={() => { if (!saving) { setDialogOpen(false); setForm(emptyForm); setEditing(null); setCustDialogTab("basics"); } }}
              disabled={saving}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60"
            >
              {custDialogTab === "revenue" ? "Close" : "Cancel"}
            </button>
            {custDialogTab !== "revenue" && (
              <button
                data-testid="customer-form-save"
                onClick={save}
                disabled={saving || !form.name.trim()}
                className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60"
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Add customer"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="customer-delete-dialog" className="bg-graphite-900 border-white/10 text-ink-primary">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove customer?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary">
              {confirmDelete?.name} will be soft-deleted. Data is retained but they'll no longer appear in this list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="customer-delete-cancel" className="bg-graphite-800 border-white/10 text-ink-primary hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="customer-delete-confirm" onClick={doDelete} className="bg-red-400/90 text-graphite-950 hover:bg-red-300">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* CSV import result dialog */}
      <Dialog open={!!importResult} onOpenChange={(v) => { if (!v) setImportResult(null); }}>
        <DialogContent data-testid="customers-import-result-dialog" className="max-w-lg bg-graphite-900 border-white/10 text-ink-primary">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2">
              <CheckCircle weight="duotone" className="h-5 w-5 text-emerald-300" />
              CSV import complete
            </DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Here's what happened with your file.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-md border border-white/10 bg-graphite-950 p-3">
                <div className="text-[10px] uppercase tracking-widest text-ink-secondary">Total rows</div>
                <div data-testid="import-result-total" className="mt-1 font-display text-2xl">{importResult?.total ?? 0}</div>
              </div>
              <div className="rounded-md border border-emerald-400/25 bg-emerald-400/5 p-3">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300/80">Imported</div>
                <div data-testid="import-result-imported" className="mt-1 font-display text-2xl text-emerald-200">{importResult?.imported ?? 0}</div>
              </div>
              <div className="rounded-md border border-amber-400/25 bg-amber-400/5 p-3">
                <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Skipped</div>
                <div data-testid="import-result-skipped" className="mt-1 font-display text-2xl text-amber-200">{importResult?.skipped ?? 0}</div>
              </div>
            </div>
            {importResult?.skipped > 0 && (
              <div className="rounded-md border border-white/10 bg-graphite-950 p-3">
                <div className="text-[10px] uppercase tracking-widest text-ink-secondary mb-2">Skip reasons</div>
                <ul className="text-xs text-ink-secondary space-y-1" data-testid="import-result-reasons">
                  {Object.entries(importResult.skipped_summary || {}).map(([reason, count]) => (
                    <li key={reason} className="flex items-center justify-between">
                      <span>{reason}</span>
                      <span className="font-mono text-ink-primary">{count}</span>
                    </li>
                  ))}
                </ul>
                {importResult.skipped_details && importResult.skipped_details.length > 0 && (
                  <details className="mt-2 text-[11px] text-ink-secondary">
                    <summary className="cursor-pointer">Row-by-row detail</summary>
                    <div className="mt-2 max-h-40 overflow-y-auto space-y-0.5 font-mono">
                      {importResult.skipped_details.map((s, i) => (
                        <div key={i}>row {s.row}: {s.reason}</div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              data-testid="import-result-close"
              onClick={() => setImportResult(null)}
              className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100"
            >
              Done
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
